import { Component, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { OpeningHours } from 'src/app/modules/shared/models/visitor.place';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'ig-hours-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
  ],
  templateUrl: './hours.input.component.html',
  styleUrls: ['./hours.input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HoursInputComponent),
      multi: true,
    },
  ],
})
export class HoursInputComponent implements OnInit, ControlValueAccessor {
  days = [
    { name: 'Sunday', value: 0, abbr: 'Sun' },
    { name: 'Monday', value: 1, abbr: 'Mon' },
    { name: 'Tuesday', value: 2, abbr: 'Tue' },
    { name: 'Wednesday', value: 3, abbr: 'Wed' },
    { name: 'Thursday', value: 4, abbr: 'Thu' },
    { name: 'Friday', value: 5, abbr: 'Fri' },
    { name: 'Saturday', value: 6, abbr: 'Sat' },
  ];

  timeValues: { value: string; text: string }[] = [];
  nextDayCloseValues: { value: string; text: string }[] = [];
  isExpanded = false;

  formArray: FormArray = this.fb.array([]);
  onTouched = () => {};
  onChange = (value: OpeningHours | null) => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Generate time values (15-minute intervals from 00:00 to 23:45)
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 4; j++) {
        const minutes = j * 15;
        const timeStr = `${i.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        let hour = i >= 13 ? i - 12 : i === 0 ? 12 : i;
        const ampm = i >= 12 ? 'PM' : 'AM';
        this.timeValues.push({
          value: timeStr,
          text: `${hour}:${minutes.toString().padStart(2, '0')} ${ampm}`,
        });
      }
    }

    for (let i = 0; i <= 6; i++) {
      const startJ = i === 0 ? 1 : 0; // skip 00:00 (midnight itself is ambiguous)
      for (let j = startJ; j < 4; j++) {
        if (i === 6 && j > 0) break; // stop at 6:00 AM
        const minutes = j * 15;
        const timeStr = `${i.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        const hour = i === 0 ? 12 : i;
        const ampm = 'AM';
        this.nextDayCloseValues.push({
          value: `NEXT:${timeStr}`,
          text: `${hour}:${minutes.toString().padStart(2, '0')} ${ampm} (next day)`,
        });
      }
    }

    // Initialize form array for each day
    this.days.forEach(() => {
      const dayGroup = this.fb.group({
        enabled: [false],
        openTime: [null],
        closeTime: [null],
        open24: [false],
        spansMidnight: [false],
      });
      this.formArray.push(dayGroup);
    });

    // Subscribe to changes
    this.formArray.valueChanges.subscribe(() => {
      this.onChange(this.convertToOpeningHours());
    });

    // react to openTime/spansMidnight changes to keep closeTime valid
    this.formArray.controls.forEach((control) => {
      const group = control as FormGroup;
      group.get('openTime')?.valueChanges.subscribe(() => this.ensureValidClose(group));
      group.get('spansMidnight')?.valueChanges.subscribe(() => this.ensureValidClose(group));
    });
  }

  writeValue(value: OpeningHours | null): void {
    if (!value) {
      this.formArray.controls.forEach((control) => {
        control.patchValue({ enabled: false, openTime: null, closeTime: null, open24: false, spansMidnight: false }, { emitEvent: false });
      });
      return;
    }

    // Populate from weekday_text if available
    if (value.weekday_text && value.weekday_text.length > 0) {
      value.weekday_text.forEach((text) => {
        const match = text.match(/(\w+):\s*(.*)/);
        if (match) {
          const dayName = match[1].substring(0, 3); // Get first 3 letters
          const dayIndex = this.days.findIndex((d) => d.abbr.toLowerCase() === dayName.toLowerCase());
          
          if (dayIndex !== -1) {
            const hours = match[2];
            if (hours.toLowerCase() !== 'closed') {
              if (hours.toLowerCase().includes('24 hours')) {
                this.formArray.at(dayIndex).patchValue(
                  { enabled: true, open24: true, openTime: null, closeTime: null, spansMidnight: false },
                  { emitEvent: false }
                );
                return;
              }
              const [openTime, closeTime] = this.parseHoursText(hours);
              this.formArray.at(dayIndex).patchValue(
                {
                  enabled: true,
                  openTime: openTime,
                  closeTime: closeTime,
                  open24: false,
                  spansMidnight: false,
                },
                { emitEvent: false }
              );
            }
          }
        }
      });
    } else if (value.periods && value.periods.length > 0) {
      this.formArray.controls.forEach((control, index) => {
        const period = value.periods.find((p) => p.open.day === index);
        if (period) {
          const spansToNextDay = !!period.close && period.close.day !== period.open.day;
          const rawCloseTime = period.close ? this.formatTime(period.close.time) : null;
          const closeTimeValue = rawCloseTime && spansToNextDay ? `NEXT:${rawCloseTime}` : rawCloseTime;
          control.patchValue(
            {
              enabled: true,
              openTime: this.formatTime(period.open.time),
              closeTime: closeTimeValue,
              open24: false,
              spansMidnight: spansToNextDay,
            },
            { emitEvent: false }
          );
        }
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private parseHoursText(text: string): [string | null, string | null] {
    const regex = /(\d{1,2}):?(\d{2})?\s*(AM|PM)\s*-\s*(\d{1,2}):?(\d{2})?\s*(AM|PM)(\s*\(next day\))?/i;
    const match = text.match(regex);

    if (match) {
      const [, openH, openM = '00', openAmPm, closeH, closeM = '00', closeAmPm, nextDayTag] = match;
      let openHour = parseInt(openH);
      let closeHour = parseInt(closeH);

      if (openAmPm.toUpperCase() === 'PM' && openHour !== 12) openHour += 12;
      if (openAmPm.toUpperCase() === 'AM' && openHour === 12) openHour = 0;

      if (closeAmPm.toUpperCase() === 'PM' && closeHour !== 12) closeHour += 12;
      if (closeAmPm.toUpperCase() === 'AM' && closeHour === 12) closeHour = 0;

      const openTime = `${openHour.toString().padStart(2, '0')}:${openM.padStart(2, '0')}`;
      const rawCloseTime = `${closeHour.toString().padStart(2, '0')}:${closeM.padStart(2, '0')}`;
      const closeTime = nextDayTag ? `NEXT:${rawCloseTime}` : rawCloseTime;

      return [openTime, closeTime];
    }

    return [null, null];
  }

  private formatTime(time: string): string {
    // Convert HHMM to HH:MM
    if (time && time.length === 4) {
      return `${time.substring(0, 2)}:${time.substring(2)}`;
    }
    return time;
  }

  private hhmm(value: string | null): string | null {
    if (!value) return null;
    const raw = value.startsWith('NEXT:') ? value.slice(5) : value;
    return raw.replace(':', '');
  }

  private isCloseValid(openTime: string | null, closeTime: string | null, spansMidnight: boolean): boolean {
    if (!openTime || !closeTime) return true;
    if (closeTime.startsWith('NEXT:') || spansMidnight) return true;
    const open = this.hhmm(openTime)!;
    const close = this.hhmm(closeTime)!;
    return close > open;
  }

  private ensureValidClose(group: FormGroup): void {
    const openTime = group.get('openTime')?.value as string | null;
    const closeTime = group.get('closeTime')?.value as string | null;
    const spansMidnight = !!group.get('spansMidnight')?.value;
    if (!this.isCloseValid(openTime, closeTime, spansMidnight)) {
      group.get('closeTime')?.setValue(null);
    }
  }

  getSameDayCloseTimes(index: number): { value: string; text: string }[] {
    const group = this.getDayFormGroup(index);
    const openTime = group.get('openTime')?.value as string | null;
    if (!openTime) return this.timeValues;
    const openHHMM = openTime.replace(':', '');
    return this.timeValues.filter((t) => t.value.replace(':', '') > openHHMM);
  }

  getFilteredCloseTimes(index: number): { value: string; text: string }[] {
    return [...this.getSameDayCloseTimes(index), ...this.nextDayCloseValues];
  }

  private convertToOpeningHours(): OpeningHours {
    const periods: any[] = [];
    const weekday_text: string[] = [];

    this.formArray.controls.forEach((control, index) => {
      const day = this.days[index];
      const { enabled, openTime, closeTime, open24, spansMidnight } = control.value;

      if (enabled && open24) {
        // 24 hours
        periods.push({
          open: { day: day.value, time: '0000' },
          close: { day: day.value, time: '2359' },
        });
        weekday_text.push(`${day.name}: Open 24 hours`);
      } else if (enabled && openTime && closeTime) {
        const isNextDay = closeTime.startsWith('NEXT:');
        const rawCloseTime = isNextDay ? closeTime.slice(5) : closeTime;
        const effectiveSpansMidnight = isNextDay || spansMidnight;

        const openHHMM = openTime.replace(':', '');
        const closeHHMM = rawCloseTime.replace(':', '');

        if (closeHHMM <= openHHMM && !effectiveSpansMidnight) {
          (control as FormGroup).get('closeTime')?.setErrors({ endBeforeStart: true });
          weekday_text.push(`${day.name}: Invalid hours`);
          return;
        } else {
          (control as FormGroup).get('closeTime')?.setErrors(null);
        }
        const closeDay = effectiveSpansMidnight ? (day.value + 1) % 7 : day.value;

        periods.push({
          open: { day: day.value, time: openHHMM },
          close: { day: closeDay, time: closeHHMM },
        });

        const openFormatted = this.formatTimeForDisplay(openTime);
        const closeFormatted = this.formatTimeForDisplay(rawCloseTime);
        const midnightNote = closeDay !== day.value ? ' (next day)' : '';
        weekday_text.push(`${day.name}: ${openFormatted} - ${closeFormatted}${midnightNote}`);
      } else {
        weekday_text.push(`${day.name}: Closed`);
      }
    });

    return {
      open_now: false,
      periods: periods,
      weekday_text: weekday_text,
    };
  }

  private formatTimeForDisplay(time: string): string {
    const raw = time.startsWith('NEXT:') ? time.slice(5) : time;
    const [hours, minutes] = raw.split(':');
    let hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minutes} ${ampm}`;
  }

  getDayFormGroup(index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }
}

